import PageHeader from "@/components/common/PageHeader";
import { moduleService } from "@/services/module.service";
import { subscriptionService } from "@/services/subscription.service";
import { tenantService } from "@/services/tenant.service";
import type { ModuleCategory, SystemModule } from "@/types/modules";
import { MODULE_CATEGORIES } from "@/types/modules";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const optionalUppercaseIdentifier = (pattern: RegExp, message: string) =>
  z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .refine((value) => !value || pattern.test(value), message)
    .transform((value) => value || undefined);

const onboardingSchema = z.object({
  businessName: z.string().min(2, "Business name is required"),
  gstin: z
    .string()
    .regex(
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
      "Invalid GSTIN format",
    ),
  drugLicenseNumber: z
    .string()
    .trim()
    .optional()
    .transform((value) => value || undefined),
  licenseExpiry: z
    .string()
    .optional()
    .transform((value) => value || undefined),
  businessEmail: z.string().trim().email("Valid business email required"),
  businessPhone: z
    .string()
    .trim()
    .regex(/^[6-9]\d{9}$/, "Invalid Indian business phone number"),
  panNumber: optionalUppercaseIdentifier(
    /^[A-Z]{5}[0-9]{4}[A-Z]$/,
    "Invalid PAN format",
  ),
  cinNumber: optionalUppercaseIdentifier(
    /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
    "Invalid CIN format",
  ),
  businessAddress: z.string().trim().min(2, "Registered address is required"),
  businessCity: z.string().trim().min(2, "Business city is required"),
  businessState: z.string().trim().min(2, "Business state is required"),
  businessPincode: z
    .string()
    .trim()
    .regex(/^[1-9][0-9]{5}$/, "Invalid business pincode"),
  isCompositeDealer: z.boolean().optional(),
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email required"),
  phoneNumber: z.string().regex(/^[6-9]\d{9}$/, "Invalid Indian phone number"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
      "Password must contain uppercase, lowercase, number, and special character",
    ),
  storeName: z.string().min(2, "Store name is required"),
  storeAddress: z.string().min(2, "Store address is required"),
  pincode: z.string().regex(/^[1-9][0-9]{5}$/, "Invalid pincode"),
  city: z.string().min(2, "City is required"),
  state: z.string().min(2, "State is required"),
  planId: z.string().min(1, "Select a plan"),
  interval: z.enum(["monthly", "yearly"]),
  useTrial: z.boolean().optional(),
});

type OnboardingForm = z.infer<typeof onboardingSchema>;

function TenantOnboardingWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [planModules, setPlanModules] = useState<SystemModule[]>([]);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<OnboardingForm>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      interval: "monthly",
      useTrial: true,
      isCompositeDealer: false,
    },
  });

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ payload, key }: { payload: OnboardingForm; key: string }) =>
      tenantService.onboard(payload, key),
  });

  const { data: plans } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: () => subscriptionService.listPlans(),
  });

  const { data: _allModules } = useQuery({
    queryKey: ["admin-modules"],
    queryFn: moduleService.getAllModules,
  });

  const selectedPlanId = watch("planId");
  const selectedInterval = watch("interval");
  const selectedPlan = plans?.find((plan) => plan.id === selectedPlanId);

  // Fetch plan modules when plan changes
  useEffect(() => {
    if (selectedPlanId) {
      moduleService
        .getPlanModules(selectedPlanId)
        .then((modules) => {
          setPlanModules(modules);
        })
        .catch(() => {
          setPlanModules([]);
        });
    }
  }, [selectedPlanId]);

  useEffect(() => {
    if (!selectedPlanId && plans?.length) {
      setValue("planId", plans[0].id);
    }
  }, [plans, selectedPlanId, setValue]);

  useEffect(() => {
    if (selectedPlan && selectedPlan.trialDays === 0) {
      setValue("useTrial", false);
    }
  }, [selectedPlan, setValue]);

  const onSubmit = async (values: OnboardingForm) => {
    try {
      // The backend onboard() method already creates the subscription
      // based on planId, interval, and useTrial - no separate call needed
      const storageKey = `tenant-activation:${values.gstin}`;
      const fingerprint = JSON.stringify(values);
      const stored = sessionStorage.getItem(storageKey);
      let parsed: { fingerprint: string; key: string } | null = null;
      try {
        parsed = stored ? JSON.parse(stored) : null;
      } catch {
        sessionStorage.removeItem(storageKey);
      }
      const key =
        parsed?.fingerprint === fingerprint ? parsed.key : crypto.randomUUID();
      sessionStorage.setItem(storageKey, JSON.stringify({ fingerprint, key }));
      const tenant = await mutateAsync({ payload: values, key });
      toast.success(
        tenant.activation?.replayed
          ? "Tenant activation result restored"
          : "Tenant activated with pharmacy defaults",
      );
      navigate(`/tenants/${tenant.id}`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create tenant";
      if (/license|business email|pan/i.test(message)) {
        setCurrentStep(1);
      }
      toast.error(message);
    }
  };

  const nextStep = async () => {
    let fieldsToValidate: (keyof OnboardingForm)[] = [];
    if (currentStep === 1) {
      fieldsToValidate = [
        "businessName",
        "gstin",
        "drugLicenseNumber",
        "licenseExpiry",
        "businessEmail",
        "businessPhone",
        "panNumber",
        "cinNumber",
        "businessAddress",
        "businessCity",
        "businessState",
        "businessPincode",
      ];
    } else if (currentStep === 2) {
      fieldsToValidate = ["planId", "interval"];
    } else if (currentStep === 3) {
      fieldsToValidate = [
        "firstName",
        "lastName",
        "email",
        "phoneNumber",
        "password",
      ];
    }
    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Group plan modules by category
  const groupedPlanModules = planModules.reduce(
    (acc, module) => {
      const category = module.category as ModuleCategory;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(module);
      return acc;
    },
    {} as Record<ModuleCategory, SystemModule[]>,
  );

  const steps = [
    { number: 1, title: "Business Info" },
    { number: 2, title: "Plan Selection" },
    { number: 3, title: "Owner Details" },
    { number: 4, title: "Store Setup" },
  ];

  return (
    <div className="space-y-8">
      <PageHeader
        title="Tenant onboarding"
        subtitle="Create a new pharmacy tenant with its primary store and owner."
      />

      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-2">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                currentStep >= step.number
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              {step.number}
            </div>
            <span
              className={`ml-2 text-sm ${
                currentStep >= step.number ? "text-slate-900" : "text-slate-400"
              }`}
            >
              {step.title}
            </span>
            {index < steps.length - 1 && (
              <div
                className={`mx-4 h-0.5 w-8 ${
                  currentStep > step.number ? "bg-slate-900" : "bg-slate-200"
                }`}
              />
            )}
          </div>
        ))}
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      >
        {/* Step 1: Business Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Business Information
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Capture the tenant&apos;s legal identity, licensing, contact,
                and registered-office details.
              </p>
            </div>

            <section className="space-y-4">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Legal identity & tax
              </h4>
              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Legal business name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("businessName")}
                    placeholder="Akshaya Community Pharmacy"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  />
                  {errors.businessName && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.businessName.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    GSTIN <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("gstin")}
                    placeholder="32AAAAA0000A1Z5"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm uppercase"
                  />
                  {errors.gstin && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.gstin.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    PAN (optional)
                  </label>
                  <input
                    {...register("panNumber")}
                    placeholder="AAAAA0000A"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm uppercase"
                  />
                  {errors.panNumber && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.panNumber.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    CIN (optional)
                  </label>
                  <input
                    {...register("cinNumber")}
                    placeholder="U12345KL2026PTC123456"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm uppercase"
                  />
                  {errors.cinNumber && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.cinNumber.message}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-slate-100 pt-5">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Pharmacy licensing
              </h4>
              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Drug license number (optional)
                  </label>
                  <input
                    {...register("drugLicenseNumber")}
                    placeholder="DL-KL-2026-001"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  />
                  {errors.drugLicenseNumber && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.drugLicenseNumber.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    License expiry (optional)
                  </label>
                  <input
                    {...register("licenseExpiry")}
                    type="date"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  />
                  {errors.licenseExpiry && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.licenseExpiry.message}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-slate-100 pt-5">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Business contact
              </h4>
              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Business email <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("businessEmail")}
                    type="email"
                    placeholder="accounts@pharmacy.example"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  />
                  {errors.businessEmail && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.businessEmail.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Business phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("businessPhone")}
                    inputMode="numeric"
                    placeholder="9876543210"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  />
                  {errors.businessPhone && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.businessPhone.message}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-slate-100 pt-5">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Registered office
              </h4>
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="lg:col-span-2">
                  <label className="text-sm font-medium text-slate-700">
                    Registered address <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("businessAddress")}
                    placeholder="Building, street, locality"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  />
                  {errors.businessAddress && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.businessAddress.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("businessCity")}
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  />
                  {errors.businessCity && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.businessCity.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("businessState")}
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  />
                  {errors.businessState && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.businessState.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("businessPincode")}
                    inputMode="numeric"
                    placeholder="695581"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                  />
                  {errors.businessPincode && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.businessPincode.message}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <input
                {...register("isCompositeDealer")}
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
              />
              <span>
                Register this tenant as a composition dealer
                <span className="mt-1 block text-xs text-slate-500">
                  Composition dealers issue bills of supply and regular GST
                  return filing is disabled.
                </span>
              </span>
            </label>
          </div>
        )}

        {/* Step 2: Plan Selection */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-slate-900">
              Select a Plan
            </h3>

            {/* Plan Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {plans?.map((plan) => (
                <label
                  key={plan.id}
                  className={`relative cursor-pointer rounded-xl border-2 p-4 transition ${
                    selectedPlanId === plan.id
                      ? "border-slate-900 bg-slate-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    {...register("planId")}
                    value={plan.id}
                    className="sr-only"
                  />
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-slate-900">
                        {plan.name}
                      </h4>
                      <span className="inline-block rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        {plan.type}
                      </span>
                    </div>
                    {selectedPlanId === plan.id && (
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-white">
                        ✓
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <div className="text-2xl font-bold text-slate-900">
                      ₹
                      {selectedInterval === "yearly"
                        ? (plan.priceYearly ?? "0")
                        : (plan.priceMonthly ?? "0")}
                      <span className="text-sm font-normal text-slate-500">
                        /{selectedInterval === "yearly" ? "year" : "month"}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1 text-xs text-slate-600">
                    <div>• {plan.maxStores} store(s)</div>
                    <div>• {plan.maxUsers} user(s)</div>
                    <div>• {plan.storageGB} GB storage</div>
                    {plan.trialDays > 0 && (
                      <div className="text-green-600">
                        • {plan.trialDays}-day free trial
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
            {errors.planId && (
              <p className="text-xs text-red-500">{errors.planId.message}</p>
            )}

            {/* Billing Interval */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-slate-700">
                Billing:
              </label>
              <div className="flex gap-2">
                <label
                  className={`cursor-pointer rounded-lg px-4 py-2 text-sm font-medium transition ${
                    selectedInterval === "monthly"
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  <input
                    type="radio"
                    {...register("interval")}
                    value="monthly"
                    className="sr-only"
                  />
                  Monthly
                </label>
                <label
                  className={`cursor-pointer rounded-lg px-4 py-2 text-sm font-medium transition ${
                    selectedInterval === "yearly"
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  <input
                    type="radio"
                    {...register("interval")}
                    value="yearly"
                    className="sr-only"
                  />
                  Yearly (save 15%)
                </label>
              </div>
            </div>

            {/* Trial Option */}
            {selectedPlan?.trialDays ? (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" {...register("useTrial")} />
                Start with {selectedPlan.trialDays}-day free trial
              </label>
            ) : null}

            {/* Plan Modules Preview */}
            {planModules.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 text-sm font-semibold text-slate-900">
                  Included Modules
                </h4>
                <div className="space-y-3">
                  {Object.entries(groupedPlanModules).map(
                    ([category, mods]) => (
                      <div key={category}>
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${
                            MODULE_CATEGORIES[category as ModuleCategory]
                              ?.color || "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {MODULE_CATEGORIES[category as ModuleCategory]
                            ?.label || category}
                        </span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {mods.map((mod) => (
                            <span
                              key={mod.code}
                              className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-600"
                            >
                              {mod.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Owner Details */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-slate-900">
              Owner Details
            </h3>
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">
                  First name
                </label>
                <input
                  {...register("firstName")}
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                />
                {errors.firstName && (
                  <p className="text-xs text-red-500">
                    {errors.firstName.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Last name
                </label>
                <input
                  {...register("lastName")}
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                />
                {errors.lastName && (
                  <p className="text-xs text-red-500">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  type="email"
                  {...register("email")}
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                />
                {errors.email && (
                  <p className="text-xs text-red-500">{errors.email.message}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Phone number
                </label>
                <input
                  {...register("phoneNumber")}
                  placeholder="9876543210"
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                />
                {errors.phoneNumber && (
                  <p className="text-xs text-red-500">
                    {errors.phoneNumber.message}
                  </p>
                )}
              </div>
              <div className="lg:col-span-2">
                <label className="text-sm font-medium text-slate-700">
                  Temporary password
                </label>
                <input
                  type="password"
                  {...register("password")}
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                />
                {errors.password && (
                  <p className="text-xs text-red-500">
                    {errors.password.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-slate-400">
                  Must contain uppercase, lowercase, number, and special
                  character
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Store Setup */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-slate-900">
              Primary Store Setup
            </h3>
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="lg:col-span-2">
                <label className="text-sm font-medium text-slate-700">
                  Store name
                </label>
                <input
                  {...register("storeName")}
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                />
                {errors.storeName && (
                  <p className="text-xs text-red-500">
                    {errors.storeName.message}
                  </p>
                )}
              </div>
              <div className="lg:col-span-2">
                <label className="text-sm font-medium text-slate-700">
                  Address
                </label>
                <input
                  {...register("storeAddress")}
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                />
                {errors.storeAddress && (
                  <p className="text-xs text-red-500">
                    {errors.storeAddress.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  City
                </label>
                <input
                  {...register("city")}
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                />
                {errors.city && (
                  <p className="text-xs text-red-500">{errors.city.message}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  State
                </label>
                <input
                  {...register("state")}
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                />
                {errors.state && (
                  <p className="text-xs text-red-500">{errors.state.message}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Pincode
                </label>
                <input
                  {...register("pincode")}
                  placeholder="560001"
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-4 text-sm"
                />
                {errors.pincode && (
                  <p className="text-xs text-red-500">
                    {errors.pincode.message}
                  </p>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
              <h4 className="mb-2 text-sm font-semibold text-green-900">
                Summary
              </h4>
              <div className="space-y-1 text-sm text-green-800">
                <div>
                  <span className="font-medium">Plan:</span>{" "}
                  {selectedPlan?.name} ({selectedInterval})
                </div>
                <div>
                  <span className="font-medium">Price:</span> ₹
                  {selectedInterval === "yearly"
                    ? selectedPlan?.priceYearly
                    : selectedPlan?.priceMonthly}
                  /{selectedInterval === "yearly" ? "year" : "month"}
                </div>
                <div>
                  <span className="font-medium">Modules:</span>{" "}
                  {planModules.length} included
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between border-t border-slate-100 pt-4">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={prevStep}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              Back
            </button>
          ) : (
            <div />
          )}

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Continue
            </button>
          ) : (
            <button
              type="submit"
              disabled={isPending}
              className="rounded-xl bg-green-600 px-6 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isPending ? "Creating..." : "Create Tenant"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default TenantOnboardingWizard;
